import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, CalendarDays, Car, UserRound, Plane, Building2, ArrowRight } from "lucide-react";
import { defaultDates, BRAND, DRIVER_PER_DAY, inr } from "@/lib/brand";
import { DateTimeField } from "@/components/site/DateTimeField";

const MODES = [
  { key: "self", label: "Self-Drive", icon: Car },
  { key: "driver", label: "With Driver", icon: UserRound },
  { key: "airport", label: "Airport Transfer", icon: Plane },
  { key: "corporate", label: "Corporate", icon: Building2 },
];
const AIRPORT = "Kempegowda International Airport (BLR)";

function Field({ icon: Icon, label, children }) {
  return (
    <label className="flex min-w-0 items-center gap-3 px-4 py-3 transition-colors duration-200 hover:bg-hc-mint/50">
      <Icon className="h-5 w-5 shrink-0 text-hc-green" />
      <span className="w-full min-w-0">
        <span className="block text-[11px] font-medium uppercase tracking-wide text-hc-slate">{label}</span>
        {children}
      </span>
    </label>
  );
}

function CorporatePanel() {
  return (
    <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" data-testid="search-corporate-panel">
      <div>
        <p className="font-display text-lg font-bold text-hc-ink">Mobility for your team</p>
        <p className="mt-1 max-w-xl text-sm text-hc-slate">Monthly GST invoicing, chauffeur-driven and self-drive fleets, dedicated account manager.</p>
      </div>
      <Link to="/business" className="btn-primary shrink-0" data-testid="search-corporate-cta">Get a corporate quote <ArrowRight className="h-4 w-4" /></Link>
    </div>
  );
}

export function SearchBar({ cities = [], initial = {}, compact = false }) {
  const d = defaultDates();
  const [mode, setMode] = useState(initial.airport ? "airport" : initial.driver ? "driver" : "self");
  const [city, setCity] = useState(initial.city || "");
  const [pickup, setPickup] = useState(initial.pickup || d.pickup);
  const [ret, setRet] = useState(initial.ret || d.ret);
  const navigate = useNavigate();
  const go = (e) => {
    e.preventDefault();
    const p = new URLSearchParams({ pickup, return: ret });
    if (city) p.set("city", city);
    if (mode === "driver" || mode === "airport") p.set("driver", "1");
    if (mode === "airport") p.set("airport", "1");
    navigate(`/cars?${p.toString()}`);
  };
  const field = "w-full truncate bg-transparent text-sm font-semibold text-hc-ink outline-none xl:text-[15px]";
  return (
    <div data-testid="search-widget">
      <div className="grid w-full grid-cols-2 gap-1 rounded-t-2xl bg-white px-2 pt-2 sm:flex sm:w-fit">
        {MODES.map((m) => (
          <button key={m.key} type="button" onClick={() => setMode(m.key)} data-testid={`search-mode-${m.key}`}
            className={`flex shrink-0 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-colors duration-200 sm:px-5 sm:text-sm ${mode === m.key ? "bg-hc-green text-white shadow-md" : "text-hc-ink/75 hover:bg-hc-mint hover:text-hc-green"}`}>
            <m.icon className="h-4 w-4" />{m.label}
          </button>
        ))}
      </div>
      <div className="rounded-2xl rounded-tl-none bg-white shadow-[0_24px_60px_-20px_rgba(11,61,38,0.35)] ring-1 ring-hc-sage">
        {mode === "corporate" ? <CorporatePanel /> : (
          <form onSubmit={go} data-testid="search-form" className={`grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center ${compact ? "p-3" : "p-4 sm:p-5"}`}>
            <div className="grid divide-y divide-hc-sage overflow-hidden rounded-xl border border-hc-sage sm:grid-cols-[1fr_1fr_1fr] sm:divide-x sm:divide-y-0">
              <Field icon={mode === "airport" ? Plane : MapPin} label={mode === "airport" ? "Airport" : "Pickup location"}>
                {mode === "airport" ? <span className={`${field} block`} data-testid="search-airport">{AIRPORT}</span> : (
                  <select data-testid="search-city" value={city} onChange={(e) => setCity(e.target.value)} className={field}>
                    <option value="">{cities[0] || BRAND.city}</option>
                    {cities.slice(1).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
              </Field>
              <Field icon={CalendarDays} label="Pickup date & time">
                <DateTimeField hideIcon testId="search-pickup" value={pickup} onChange={setPickup} className={field} />
              </Field>
              <Field icon={CalendarDays} label="Return date & time">
                <DateTimeField hideIcon testId="search-return" value={ret} onChange={setRet} className={field} />
              </Field>
            </div>
            <button data-testid="search-submit" type="submit" className="btn-primary !px-8 !py-4 !text-base">
              <Search className="h-5 w-5" /> Search Cars
            </button>
            {mode === "driver" && <p className="text-xs text-hc-slate lg:col-span-2" data-testid="search-driver-note">A professional driver is assigned by Hoorecars · {inr(DRIVER_PER_DAY)} per day extra</p>}
            {mode === "airport" && <p className="text-xs text-hc-slate lg:col-span-2" data-testid="search-airport-note">Chauffeur-driven pickup or drop at BLR airport · add your flight details in the pickup address at checkout · {inr(DRIVER_PER_DAY)} per day driver charge</p>}
          </form>
        )}
      </div>
    </div>
  );
}
